// This file exports the DependentEntityPicker component, which allows users to select entities based on a dependency reference. It uses the Select component from Material-UI and fetches entities from the catalog API based on filters.
import { useEffect } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';

export const dependentEntityPickerSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      dependsOn: z
        .string()
        .optional()
        .describe(
          'The field name in the form data to depend on, defaults to "team"',
        ),
      catalogFilter: z
        .union([
          z.record(z.string(), z.union([z.string(), z.array(z.string())])),
          z.array(
            z.record(z.string(), z.union([z.string(), z.array(z.string())])),
          ),
        ])
        .optional()
        .describe(
          'List of key-value filter pairs or a single object filter for the catalog request',
        ),
    }),
});

export const DependentEntityPicker = ({
  formData,
  onChange,
  formContext,
  uiSchema,
  idSchema,
  schema,
  rawErrors,
  required,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  // Read properties from the yaml ui:options
  const options = uiSchema?.['ui:options'] || {};
  const dependsOnField = (options.dependsOn as string) || 'team';
  const baseFilters =
    (options.catalogFilter as Record<string, string | string[]>[]) || [];
  const baseFiltersString = JSON.stringify(baseFilters);

  const dependencyRef = formContext.formData[dependsOnField];

  const { value: entities, loading } = useAsync(async () => {
    if (!dependencyRef) {
      return [];
    }

    // Extract namespace from dependency (expected format `kind:namespace/name`)
    const parts = dependencyRef.split('/');
    const namespace = parts.length > 1 ? parts[0].split(':')[1] : 'default';

    // Merge the filters defined in YAML with the dynamic namespace constraint
    const parsedFilters = JSON.parse(baseFiltersString);
    const filter =
      parsedFilters.length > 0
        ? parsedFilters.map((f: any) => ({
            ...f,
            'metadata.namespace': namespace,
          }))
        : [{ 'metadata.namespace': namespace }];

    const response = await catalogApi.getEntities({ filter });
    return response.items.map(
      entity =>
        `${entity.kind.toLocaleLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`,
    );
  }, [dependencyRef, catalogApi, baseFiltersString]);

  useEffect(() => {
    if (loading) return;

    const currentEntities = entities || [];
    if (!dependencyRef) {
      if (formData) {
        onChange('');
      }
    } else if (formData && !currentEntities.includes(formData)) {
      onChange('');
    }
  }, [loading, dependencyRef, entities, formData, onChange]);

  const displayEntities = entities || [];

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <Autocomplete
        id={idSchema.$id}
        options={displayEntities}
        value={formData || null}
        onChange={(_, newValue) => onChange((newValue as string) || '')}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title ?? 'Select an entity'}
            required={required}
            error={rawErrors?.length > 0 && !formData}
            inputProps={{
              ...params.inputProps,
              'aria-describedby': schema.description
                ? `${idSchema.$id}-description`
                : undefined,
            }}
          />
        )}
      />
      {schema.description && (
        <FormHelperText id={`${idSchema.$id}-description`}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};
